import { useRef, useState, type DragEvent, type ReactNode } from 'react';

import type { SnapSettings } from './canvas';
import { GeometryToolIcon } from './GeometryToolIcon';
import type { GeometrySelection, GeometryTool } from './interaction';
import { pointsFor, type GeometryModel } from './model';

type GroupId = 'create' | 'openings' | 'edit' | 'precision' | 'view';

type HistoryControls = {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel?: string;
  redoLabel?: string;
  undo: () => void;
  redo: () => void;
};

const INITIAL_ROWS: GroupId[][] = [['create', 'openings', 'edit', 'precision', 'view']];

export function GeometryToolPalette({
  tool,
  onTool,
  snapSettings,
  onSnapChange,
  selection,
  model,
  history,
  onInsertVertex,
  onDeleteSelected,
  onFit,
  onFitSelection,
}: {
  tool: GeometryTool;
  onTool: (tool: GeometryTool) => void;
  snapSettings: SnapSettings;
  onSnapChange: (settings: SnapSettings) => void;
  selection: GeometrySelection | undefined;
  model: GeometryModel;
  history: HistoryControls;
  onInsertVertex: () => void;
  onDeleteSelected: () => void;
  onFit: () => void;
  onFitSelection: () => void;
}) {
  const [rows, setRows] = useState<GroupId[][]>(INITIAL_ROWS);
  const draggingRef = useRef<GroupId>();
  const canDelete = selection?.kind === 'vertex' || selection?.kind === 'opening';
  const canDeleteVertex = selection?.kind === 'vertex' && pointsFor(model, selection.owner).length > 3;
  const canInsert = selection?.kind === 'segment';
  const hasSelection = Boolean(selection);

  const toolButton = (name: GeometryTool, label: string, title: string, disabled = false) => (
    <button type="button" className={tool === name ? 'geometry-tool-button is-active' : 'geometry-tool-button'} aria-pressed={tool === name} disabled={disabled} title={title} onClick={() => onTool(name)}>
      <GeometryToolIcon name={name} /><span>{label}</span>
    </button>
  );
  const toggleButton = (name: 'grid' | 'snap-point' | 'ortho', label: string, active: boolean, title: string, onClick: () => void) => (
    <button type="button" className={active ? 'geometry-tool-button is-active' : 'geometry-tool-button'} aria-pressed={active} title={title} onClick={onClick}>
      <GeometryToolIcon name={name} /><span>{label}</span>
    </button>
  );

  const groupContent: Record<GroupId, { title: string; content: ReactNode }> = {
    create: {
      title: 'Create',
      content: <>
        {toolButton('select', 'Select / Move', 'Select and move vertices or openings · V')}
        {toolButton('draw-roof-rectangle', 'Rectangle', 'Draw rectangular roof · R')}
        {toolButton('draw-roof-polygon', 'Polygon', 'Draw polygon roof · P')}
      </>,
    },
    openings: {
      title: 'Openings',
      content: <>
        {toolButton('draw-opening-rectangle', 'Rect. Opening', 'Draw rectangular opening · Shift+R', !model.closed)}
        {toolButton('draw-opening-polygon', 'Poly. Opening', 'Draw polygon opening · Shift+P', !model.closed)}
      </>,
    },
    edit: {
      title: 'Edit',
      content: <>
        {toolButton('insert-vertex', 'Add Vertex', 'Insert a vertex on an edge · I', !model.closed)}
        <button type="button" className="geometry-tool-button" disabled={!canDeleteVertex} title="Delete selected vertex" onClick={onDeleteSelected}><GeometryToolIcon name="delete-vertex" /><span>Delete Vertex</span></button>
        {toolButton('move-opening', 'Move Opening', 'Move an opening as one rigid shape', model.openings.length === 0)}
        <button type="button" className="geometry-tool-button" disabled={!canDelete} title="Delete selected opening or vertex · Delete" onClick={onDeleteSelected}><GeometryToolIcon name="delete-selected" /><span>Delete Selected</span></button>
        <button type="button" className="geometry-tool-button" disabled={!canInsert} title="Insert a vertex at the selected edge midpoint" onClick={onInsertVertex}><GeometryToolIcon name="insert-vertex" /><span>Insert Here</span></button>
      </>,
    },
    precision: {
      title: 'Precision',
      content: <>
        {toggleButton('grid', 'Grid', snapSettings.toGrid, 'Toggle grid snapping', () => onSnapChange({ ...snapSettings, toGrid: !snapSettings.toGrid }))}
        <label className="geometry-grid-size" title="Grid spacing used for display and snapping">
          <input
            type="number"
            min="1"
            step="1"
            aria-label="Grid size in millimetres"
            value={snapSettings.gridSizeMM}
            onChange={(event) => {
              const parsed = Number(event.target.value);
              if (Number.isFinite(parsed) && parsed > 0) onSnapChange({ ...snapSettings, gridSizeMM: parsed });
            }}
          />
          <span>mm</span>
        </label>
        {toggleButton('snap-point', 'Snap', snapSettings.toPoints, 'Toggle vertex snapping', () => onSnapChange({ ...snapSettings, toPoints: !snapSettings.toPoints }))}
        {toggleButton('ortho', 'Ortho', snapSettings.orthogonal, 'Toggle orthogonal constraint', () => onSnapChange({ ...snapSettings, orthogonal: !snapSettings.orthogonal }))}
      </>,
    },
    view: {
      title: 'View / History',
      content: <>
        {toolButton('pan', 'Pan', 'Pan drawing · hold Space')}
        <button type="button" className="geometry-tool-button" title="Fit all · Ctrl+0" onClick={onFit}><GeometryToolIcon name="fit-all" /><span>Fit All</span></button>
        <button type="button" className="geometry-tool-button" disabled={!hasSelection} title="Fit selected geometry" onClick={onFitSelection}><GeometryToolIcon name="fit-selection" /><span>Fit Selection</span></button>
        <button type="button" className="geometry-tool-button" disabled={!history.canUndo} title={history.undoLabel ? `Undo ${history.undoLabel}` : 'Undo'} onClick={history.undo}><GeometryToolIcon name="undo" /><span>Undo</span></button>
        <button type="button" className="geometry-tool-button" disabled={!history.canRedo} title={history.redoLabel ? `Redo ${history.redoLabel}` : 'Redo'} onClick={history.redo}><GeometryToolIcon name="redo" /><span>Redo</span></button>
      </>,
    },
  };

  const moveGroup = (groupId: GroupId, targetRow: number, before?: GroupId) => {
    setRows((current) => {
      const cleaned = current.map((row) => row.filter((id) => id !== groupId)).filter((row) => row.length > 0);
      const rowIndex = Math.min(Math.max(targetRow, 0), cleaned.length);
      if (!cleaned[rowIndex]) cleaned.splice(rowIndex, 0, []);
      const row = cleaned[rowIndex]!;
      const beforeIndex = before ? row.indexOf(before) : -1;
      if (beforeIndex >= 0) row.splice(beforeIndex, 0, groupId);
      else row.push(groupId);
      return cleaned;
    });
  };

  const dropBefore = (event: DragEvent<HTMLDivElement>, rowIndex: number, before: GroupId) => {
    event.preventDefault();
    event.stopPropagation();
    const id = draggingRef.current;
    if (id) moveGroup(id, rowIndex, before === id ? undefined : before);
    draggingRef.current = undefined;
  };

  return (
    <div className="geometry-tool-palette" aria-label="Roof geometry tools">
      {rows.map((row, rowIndex) => (
        <div
          className="geometry-tool-palette-row"
          key={`tool-row-${rowIndex}`}
          data-tool-row={rowIndex}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            const id = draggingRef.current;
            if (id) moveGroup(id, rowIndex);
            draggingRef.current = undefined;
          }}
        >
          {row.map((groupId) => {
            const group = groupContent[groupId];
            return (
              <ToolGroup
                key={groupId}
                id={groupId}
                title={group.title}
                onDragStart={(event) => {
                  draggingRef.current = groupId;
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', groupId);
                }}
                onDropBefore={(event) => dropBefore(event, rowIndex, groupId)}
              >
                {group.content}
              </ToolGroup>
            );
          })}
        </div>
      ))}
      <div
        className="geometry-tool-new-row-drop"
        aria-label="Drop a tool group here to create a lower row"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const id = draggingRef.current;
          if (id) moveGroup(id, rows.length);
          draggingRef.current = undefined;
        }}
      >
        <span>Drop group here for a new row</span>
      </div>
    </div>
  );
}

function ToolGroup({
  id,
  title,
  children,
  onDragStart,
  onDropBefore,
}: {
  id: GroupId;
  title: string;
  children: ReactNode;
  onDragStart: (event: DragEvent<HTMLDivElement>) => void;
  onDropBefore: (event: DragEvent<HTMLDivElement>) => void;
}) {
  return (
    <div className="geometry-tool-group" role="group" aria-label={title} data-tool-group={id} onDragOver={(event) => event.preventDefault()} onDrop={onDropBefore}>
      <div className="geometry-tool-group-title" draggable onDragStart={onDragStart} title="Drag to rearrange this tool group">
        <span className="geometry-tool-grip" aria-hidden="true">⠿</span>
        <span>{title}</span>
      </div>
      <div className="geometry-tool-group-buttons">{children}</div>
    </div>
  );
}
